/**
 * Pipeline Assertion Recorder — frontend counterpart.
 *
 * Records EXPECT/ACTUAL at every data handoff in the frontend pipeline:
 * - Graph store mutations (setElements, addElements)
 * - Node Inspector data flow (what data reaches each tab)
 * - Edge value mutations (old → new)
 * - User actions (click, expand, trace, search)
 *
 * Activate: add `?pipelineDebug=true` to the Workbench URL.
 * Export: included in Ctrl+Shift+E alongside canvasDebug data.
 * Access: window.__pipelineRecorder for programmatic access.
 */

export interface PipelineEntry {
  t: string;           // ISO timestamp
  ms: number;          // ms since recorder init
  stage: string;       // graph_store | inspector | edge_value | user_action | sse
  source: string;      // what triggered this entry
  expect: Record<string, any>;
  actual: Record<string, any>;
  pass: boolean;
  detail?: string;
}

let _enabled: boolean | null = null;
let _startTime = 0;
const _entries: PipelineEntry[] = [];
const MAX_ENTRIES = 3000;

export function isPipelineDebugEnabled(): boolean {
  if (_enabled === null) {
    try {
      const params = new URLSearchParams(window.location.search);
      _enabled = params.get('pipelineDebug') === 'true' || params.get('canvasDebug') === 'true';
      if (_enabled) {
        _startTime = Date.now();
        console.log(
          '%c[PIPELINE] Recording enabled — tracking all data handoffs',
          'background:#7c3aed;color:#fff;padding:2px 8px;border-radius:4px;font-weight:bold'
        );
      }
    } catch {
      _enabled = false;
    }
  }
  return _enabled;
}

function checkExpect(expect: Record<string, any>, actual: Record<string, any>): boolean {
  for (const [key, expectedVal] of Object.entries(expect)) {
    if (key.endsWith('_gte')) {
      const field = key.slice(0, -4);
      if ((actual[field] ?? 0) < expectedVal) return false;
    } else if (key.endsWith('_lte')) {
      const field = key.slice(0, -4);
      if ((actual[field] ?? Infinity) > expectedVal) return false;
    } else if (key === 'has_field') {
      const fields = Array.isArray(expectedVal) ? expectedVal : [expectedVal];
      for (const f of fields) {
        if (!(f in actual)) return false;
      }
    } else if (key === 'equals') {
      const [field, val] = expectedVal;
      if (actual[field] !== val) return false;
    } else {
      if (actual[key] !== expectedVal) return false;
    }
  }
  return true;
}

// ── Core recording function ──────────────────────────────────────

export function recordPipeline(
  stage: string,
  source: string,
  expect: Record<string, any>,
  actual: Record<string, any>,
  detail?: string,
): PipelineEntry | null {
  if (!isPipelineDebugEnabled()) return null;
  if (_startTime === 0) _startTime = Date.now();

  const passed = checkExpect(expect, actual);
  const entry: PipelineEntry = {
    t: new Date().toISOString(),
    ms: Date.now() - _startTime,
    stage,
    source,
    expect,
    actual,
    pass: passed,
    detail,
  };

  _entries.push(entry);
  if (_entries.length > MAX_ENTRIES) _entries.splice(0, 500);

  // Log failures prominently
  if (!passed) {
    console.warn(
      `%c[PIPELINE FAIL] %c${stage}/${source}`,
      'background:#ef4444;color:#fff;padding:2px 6px;border-radius:3px;font-weight:bold',
      'color:#ef4444;font-weight:bold',
      { expect, actual, detail }
    );
  }

  return entry;
}

// ── Convenience recorders for each gap ──────────────────────────

/** Gap 4: Record graph store mutation (setElements before/after) */
export function recordGraphMutation(
  source: string,
  before: { nodes: number; edges: number },
  after: { nodes: number; edges: number },
  expectedDelta: { nodes?: number; edges?: number } = {},
  detail?: string,
): void {
  const expect: Record<string, any> = {};
  if (expectedDelta.nodes !== undefined) {
    expect.nodes = before.nodes + expectedDelta.nodes;
  }
  if (expectedDelta.edges !== undefined) {
    expect.edges = before.edges + expectedDelta.edges;
  }

  recordPipeline('graph_store', source, expect, {
    nodes: after.nodes,
    edges: after.edges,
    delta_nodes: after.nodes - before.nodes,
    delta_edges: after.edges - before.edges,
  }, detail);
}

/** Gap 5: Record Node Inspector data received vs displayed.
 *
 *  De-duplicates against the prior snapshot for the same (tab, nodeId) so a
 *  React render loop in the consuming tab (the recorder is called inside
 *  TransfersTab's JSX render path, not a useEffect) doesn't burst 50+
 *  identical entries per second. Only emits when something a human cares
 *  about actually changed (counts, source, match flag).
 */
const _lastInspectorSnap: Map<string, string> = new Map();

export function recordInspectorData(
  tab: string,
  nodeId: string,
  dataReceived: { count: number; source: string },
  dataDisplayed: { count: number; filtered: number },
  detail?: string,
): void {
  const key = `${tab}:${nodeId}`;
  const snap = `${dataReceived.source}|${dataReceived.count}|${dataDisplayed.count}|${dataDisplayed.filtered}|${detail || ''}`;
  if (_lastInspectorSnap.get(key) === snap) return;
  _lastInspectorSnap.set(key, snap);

  recordPipeline('inspector', tab, {
    count_gte: 0,
  }, {
    node: nodeId?.slice(0, 16),
    source: dataReceived.source,
    received: dataReceived.count,
    displayed: dataDisplayed.count,
    filtered: dataDisplayed.filtered,
    match: dataReceived.count === dataDisplayed.count + dataDisplayed.filtered,
  }, detail);
}

/** Gap 6: Record edge value change */
export function recordEdgeValueChange(
  edgeId: string,
  oldValue: number,
  newValue: number,
  source: string,
  detail?: string,
): void {
  if (oldValue === newValue) return; // No change, skip
  recordPipeline('edge_value', source, {
    // We don't know what the "correct" value is — just record the change
  }, {
    edge: edgeId?.slice(0, 30),
    old_value: oldValue,
    new_value: newValue,
    delta: Math.round((newValue - oldValue) * 1e8) / 1e8,
    direction: newValue > oldValue ? 'increased' : 'decreased',
  }, detail);
}

/** Gap 7: Record user action */
export function recordUserAction(
  action: string,
  target: string,
  context: string,
  detail?: string,
): void {
  recordPipeline('user_action', context, {}, {
    action,
    target: target?.slice(0, 20),
  }, detail);
}

/** Record SSE event received vs rendered */
export function recordSSEEvent(
  eventType: string,
  step: string,
  received: { nodes?: number; edges?: number; value?: number },
  rendered: { nodes?: number; edges?: number },
): void {
  recordPipeline('sse', `${step}.${eventType}`, {
    // Expect rendered to match received
    ...(received.nodes !== undefined ? { nodes: received.nodes } : {}),
    ...(received.edges !== undefined ? { edges: received.edges } : {}),
  }, {
    received_nodes: received.nodes,
    received_edges: received.edges,
    rendered_nodes: rendered.nodes,
    rendered_edges: rendered.edges,
    value: received.value,
  });
}

// ── Export & access ──────────────────────────────────────────────

export function getPipelineEntries(): PipelineEntry[] {
  return _entries;
}

export function getPipelineSummary(): Record<string, any> {
  const stages: Record<string, { pass: number; fail: number; total: number }> = {};
  for (const e of _entries) {
    if (!stages[e.stage]) stages[e.stage] = { pass: 0, fail: 0, total: 0 };
    stages[e.stage].total++;
    if (e.pass) stages[e.stage].pass++;
    else stages[e.stage].fail++;
  }
  return {
    total: _entries.length,
    pass: _entries.filter(e => e.pass).length,
    fail: _entries.filter(e => !e.pass).length,
    stages,
  };
}

export function getPipelineFailures(): PipelineEntry[] {
  return _entries.filter(e => !e.pass);
}

export function clearPipelineEntries(): void {
  _entries.length = 0;
}

// Expose on window for programmatic access and CLAURST integration
if (typeof window !== 'undefined') {
  (window as any).__pipelineRecorder = {
    getEntries: getPipelineEntries,
    getSummary: getPipelineSummary,
    getFailures: getPipelineFailures,
    clear: clearPipelineEntries,
    record: recordPipeline,
  };
}
